import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, List, LayoutGrid } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/services/api';
import { toast } from 'sonner';
import { useConfirm } from '@/hooks/useConfirm';

const LocationsConfig = () => {
  const [locations, setLocations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', address: '', city: '', phone: '' });
  const [viewMode, setViewMode] = useState('list');
  const { confirm, ConfirmDialog } = useConfirm();

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await api.getLocations();
      setLocations(res.data);
    } catch (error) {
      toast.error('Error al cargar locales');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar campos obligatorios
    if (!formData.name || !formData.address || !formData.city) {
      toast.error('Por favor completa todos los campos obligatorios (*)');
      return;
    }
    
    // Limpiar datos opcionales
    const dataToSend = {
      name: formData.name,
      address: formData.address,
      city: formData.city,
      phone: formData.phone?.trim() || null
    };
    
    try {
      if (editingItem) {
        await api.updateLocation(editingItem.id, dataToSend);
        toast.success('Local actualizado');
      } else {
        await api.createLocation(dataToSend);
        toast.success('Local creado');
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Error al guardar local');
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm('Eliminar Local', '¿Estás seguro de eliminar este local? Esta acción no se puede deshacer.');
    if (!confirmed) return;
    
    try {
      await api.deleteLocation(id);
      toast.success('Local eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({ name: '', address: '', city: '', phone: '' });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Locales</h2>
        <div className="flex space-x-2">
          <div className="flex border border-[#C9A961]/30 rounded">
            <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('list')} className={viewMode === 'list' ? 'bg-[#C9A961]' : ''}>
              <List size={16} />
            </Button>
            <Button variant={viewMode === 'cards' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('cards')} className={viewMode === 'cards' ? 'bg-[#C9A961]' : ''}>
              <LayoutGrid size={16} />
            </Button>
          </div>
          <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-[#C9A961]">
            <Plus className="mr-2" size={18} /> Nuevo
          </Button>
        </div>
      </div>

      {viewMode === 'list' ? (
        <div className="border border-[#C9A961]/20 rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Dirección</TableHead>
                <TableHead>Ciudad</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {locations.map((loc) => (
                <TableRow key={loc.id}>
                  <TableCell className="font-medium">{loc.name}</TableCell>
                  <TableCell>{loc.address}</TableCell>
                  <TableCell>{loc.city}</TableCell>
                  <TableCell>{loc.phone}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-2">
                      <Button size="sm" variant="outline" onClick={() => { setEditingItem(loc); setFormData(loc); setShowModal(true); }}>
                        <Edit size={16} />
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDelete(loc.id)}>
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {locations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-[#6B6B6B] py-8">No hay locales registrados</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {locations.map((loc) => (
            <Card key={loc.id} className="p-4">
              <h3 className="font-semibold text-lg mb-2">{loc.name}</h3>
              <p className="text-sm text-[#6B6B6B]">{loc.address}</p>
              <p className="text-sm text-[#6B6B6B]">{loc.city}</p>
              <p className="text-sm text-[#6B6B6B]">{loc.phone}</p>
              <div className="flex space-x-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => { setEditingItem(loc); setFormData(loc); setShowModal(true); }}>
                  <Edit size={16} />
                </Button>
                <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDelete(loc.id)}>
                  <Trash2 size={16} />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-[#C9A961]">{editingItem ? 'Editar' : 'Nuevo'} Local</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Nombre *</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required /></div>
            <div><Label>Dirección *</Label><Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} required /></div>
            <div><Label>Ciudad *</Label><Input value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} required /></div>
            <div><Label>Teléfono</Label><Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} /></div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>Cancelar</Button>
              <Button type="submit" className="bg-[#C9A961]">Guardar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog />
    </div>
  );
};

export default LocationsConfig;
